import { databases, databaseId, appwriteConfig } from '../config/appwrite';
import { Platform } from '../types';
import { ID, Query } from 'appwrite';

export class PlatformService {
  // Alle Plattformen abrufen
  static async getAllPlatforms(): Promise<Platform[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.platforms,
        [Query.orderAsc('name'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        apiEndpoint: doc.apiEndpoint,
        apiKey: doc.apiKey,
        publishMapping: doc.publishMapping,
        isActive: doc.isActive,
        kiApiUrl: doc.kiApiUrl,
        kiApiSecret: doc.kiApiSecret,
        kiApiEnabled: doc.kiApiEnabled ?? true,
        imageGenerationWebhook: doc.imageGenerationWebhook,
        metadataWebhook: doc.metadataWebhook,
        videoToolsWebhook: doc.videoToolsWebhook,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Plattformen konnten nicht abgerufen werden');
    }
  }

  // Plattform nach ID abrufen
  static async getPlatformById(id: string): Promise<Platform> {
    try {
      const doc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.platforms,
        id
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        apiEndpoint: doc.apiEndpoint,
        apiKey: doc.apiKey,
        publishMapping: doc.publishMapping,
        isActive: doc.isActive,
        kiApiUrl: doc.kiApiUrl,
        kiApiSecret: doc.kiApiSecret,
        kiApiEnabled: doc.kiApiEnabled ?? true,
        imageGenerationWebhook: doc.imageGenerationWebhook,
        metadataWebhook: doc.metadataWebhook,
        videoToolsWebhook: doc.videoToolsWebhook,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Plattform konnte nicht abgerufen werden');
    }
  }

  // Neue Plattform erstellen
  static async createPlatform(platformData: Partial<Platform>): Promise<Platform> {
    try {
      const doc = await databases.createDocument(
        databaseId,
        appwriteConfig.collections.platforms,
        ID.unique(),
        {
          name: platformData.name,
          description: platformData.description,
          apiEndpoint: platformData.apiEndpoint,
          apiKey: platformData.apiKey,
          publishMapping: platformData.publishMapping,
          isActive: platformData.isActive ?? true,
          kiApiUrl: platformData.kiApiUrl,
          kiApiSecret: platformData.kiApiSecret,
          kiApiEnabled: platformData.kiApiEnabled ?? true,
          imageGenerationWebhook: platformData.imageGenerationWebhook,
          metadataWebhook: platformData.metadataWebhook,
          videoToolsWebhook: platformData.videoToolsWebhook,
        }
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        apiEndpoint: doc.apiEndpoint,
        apiKey: doc.apiKey,
        publishMapping: doc.publishMapping,
        isActive: doc.isActive,
        kiApiUrl: doc.kiApiUrl,
        kiApiSecret: doc.kiApiSecret,
        kiApiEnabled: doc.kiApiEnabled ?? true,
        imageGenerationWebhook: doc.imageGenerationWebhook,
        metadataWebhook: doc.metadataWebhook,
        videoToolsWebhook: doc.videoToolsWebhook,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Plattform konnte nicht erstellt werden');
    }
  }

  // Plattform aktualisieren
  static async updatePlatform(id: string, platformData: Partial<Platform>): Promise<Platform> {
    try {
      const doc = await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.platforms,
        id,
        {
          name: platformData.name,
          description: platformData.description,
          apiEndpoint: platformData.apiEndpoint,
          apiKey: platformData.apiKey,
          publishMapping: platformData.publishMapping,
          isActive: platformData.isActive,
          kiApiUrl: platformData.kiApiUrl,
          kiApiSecret: platformData.kiApiSecret,
          kiApiEnabled: platformData.kiApiEnabled,
          imageGenerationWebhook: platformData.imageGenerationWebhook,
          metadataWebhook: platformData.metadataWebhook,
          videoToolsWebhook: platformData.videoToolsWebhook,
        }
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        apiEndpoint: doc.apiEndpoint,
        apiKey: doc.apiKey,
        publishMapping: doc.publishMapping,
        isActive: doc.isActive,
        kiApiUrl: doc.kiApiUrl,
        kiApiSecret: doc.kiApiSecret,
        kiApiEnabled: doc.kiApiEnabled ?? true,
        imageGenerationWebhook: doc.imageGenerationWebhook,
        metadataWebhook: doc.metadataWebhook,
        videoToolsWebhook: doc.videoToolsWebhook,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Plattform konnte nicht aktualisiert werden');
    }
  }

  // Plattform löschen
  static async deletePlatform(id: string): Promise<void> {
    try {
      await databases.deleteDocument(
        databaseId,
        appwriteConfig.collections.platforms,
        id
      );
    } catch (error) {
      throw new Error('Plattform konnte nicht gelöscht werden');
    }
  }

  // Aktive Plattformen abrufen
  static async getActivePlatforms(): Promise<Platform[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.platforms,
        [Query.equal('isActive', true), Query.orderAsc('name'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        apiEndpoint: doc.apiEndpoint,
        apiKey: doc.apiKey,
        publishMapping: doc.publishMapping,
        isActive: doc.isActive,
        kiApiUrl: doc.kiApiUrl,
        kiApiSecret: doc.kiApiSecret,
        kiApiEnabled: doc.kiApiEnabled ?? true,
        imageGenerationWebhook: doc.imageGenerationWebhook,
        metadataWebhook: doc.metadataWebhook,
        videoToolsWebhook: doc.videoToolsWebhook,
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Aktive Plattformen konnten nicht abgerufen werden');
    }
  }
}
